'use client';

import * as React from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDownIcon } from '@radix-ui/react-icons';

import { cn } from '../../lib/utils';
import { useThumbnailLocationListener } from '@state';
import { useViewerLayoutContext } from '../../contextProviders';

const Accordion = AccordionPrimitive.Root;

const AccordionItem = ({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn(className)}
    {...props}
  />
);
AccordionItem.displayName = 'AccordionItem';

const AccordionTrigger = ({
  className,
  children,
  ref,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger>) => {
  const thumbnailLocation = useThumbnailLocationListener();
  const { isPatientMobile } = useViewerLayoutContext();
  return (
    <AccordionPrimitive.Header
      className={
        isPatientMobile
          ? 'hidden'
          : thumbnailLocation === 'top'
            ? 'mt-[4px] mb-[4px] flex h-[140px] w-[135px]'
            : 'flex'
      }
    >
      <AccordionPrimitive.Trigger
        ref={ref}
        className={cn(
          'flex flex-1 items-center justify-between py-2 px-2 text-base font-medium transition-transform duration-200',
          className,
          '[&[data-state=open]>svg]:rotate-270',
          '[&[data-state=closed]>svg]:rotate-90'
        )}
        {...props}
      >
        {props.asChild ? (
          children
        ) : (
          <>
            {children}
            <ChevronDownIcon className="h-4 w-4 shrink-0 text-[#e51b48] transition-transform duration-200" />
          </>
        )}
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
};
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = ({
  className,
  children,
  ref,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden text-base"
    {...props}
  >
    <div className={cn(className)}>{children}</div>
  </AccordionPrimitive.Content>
);
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
